import { isStableUIEnabledForTests } from '@confluence/e2e-url-override/entry-points/isStableUIEnabledForTests.ts';
import { expVal } from '@confluence/feature-experiments';

import { BaseStateContainer } from './BaseStateContainer.ts';
import {
	getBanners,
	hideBanner,
	seedBanners,
	showBanner,
	subscribeToBanners,
} from './bannerStore.ts';
import { resolveTopNavHeight } from './resolveTopNavHeight.ts';

const SERVER_RENDER_BANNER_NAME = 'ServerRenderedBanners';

export type BannerStateInfo = {
	height: number;
	name: string;
};

type BannerStateContainerState = {
	banners: BannerStateInfo[];
};

type BannerStateContainerProps = {
	hasTopNav?: boolean;
	initialState?: BannerStateContainerState;
	/**
	 * Opts this container out of the shared banner stack: reads are empty and writes are dropped.
	 * Only meaningful on the store-backed path, where state is global — on the legacy path each
	 * container already owns its own state. Used by `WithoutBanners`.
	 */
	suppressed?: boolean;
};

export const findBanner = (name: string, inArray: BannerStateInfo[]): number =>
	inArray.findIndex((banner: BannerStateInfo) => banner.name === name);

export class BannerStateContainer extends BaseStateContainer<BannerStateContainerState> {
	topNavHeight: number;

	/**
	 * Read once per instance rather than at each callsite. A mid-session flip would leave this
	 * container reading from one backing while writing to the other, losing banners outright.
	 * `topNavHeight` below is frozen at construction for the same reason.
	 */
	private readonly storeBacked: boolean;

	private readonly suppressed: boolean;

	private pendingNotify: Promise<void> | null = null;
	private unsubscribeFromStore: (() => void) | null = null;

	constructor(props: BannerStateContainerProps = {}) {
		super();
		// Resolved unconditionally, even when it is about to be discarded, so that exposure for the
		// nav-height flags is unchanged from before this was extracted into a helper.
		const navHeight = resolveTopNavHeight();
		this.topNavHeight = props.hasTopNav === false ? 0 : navHeight;
		this.suppressed = props.suppressed === true;
		this.storeBacked = expVal('confluence_banners_module_store', 'isEnabled', false);

		this.state.banners = isStableUIEnabledForTests() ? [] : props.initialState?.banners || [];

		if (this.storeBacked && !this.suppressed) {
			seedBanners(this.state.banners);
			this.state = { banners: getBanners() };

			// Mirror the store into `this.state` so every existing reader keeps working unchanged:
			// getTotalHeight, getBannerList, and the 46 `Subscribe` consumers reading state.banners.
			// The mirror is synchronous so a read straight after a flush is fresh; the notification is
			// coalesced, so a tick's worth of store changes costs one round no matter which container
			// (or, later, which hook) wrote them.
			this.unsubscribeFromStore = subscribeToBanners(() => {
				this.state = { banners: getBanners() };
				this.scheduleNotify();
			});
		}
	}

	state: BannerStateContainerState = {
		banners: [],
	};

	_updateServerRenderedBanners = (spaBannerHeight: number): void => {
		if (spaBannerHeight === 0) {
			this.hide(SERVER_RENDER_BANNER_NAME);
		} else {
			this.show(SERVER_RENDER_BANNER_NAME, spaBannerHeight);
		}
	};

	/**
	 * Adds or updates the specified banner in the list of visible banners.
	 * Add the banner is not already in the list, otherwise update the banner's height
	 *
	 * @param {string} name Unique name of the banner
	 * @param {number} height Height of the banner, in pixels
	 */
	show = (name: string, height: number): void => {
		if (isStableUIEnabledForTests()) {
			return;
		}

		if (this.storeBacked) {
			// The store defers and coalesces; a suppressed container drops the write entirely.
			if (!this.suppressed) {
				showBanner(name, height);
			}
			return;
		}

		void this.setState((state: any) => {
			const index = findBanner(name, state.banners);

			if (index === -1) {
				return {
					...state,
					banners: [...state.banners, { name, height }],
				};
			} else if (state.banners[index].height !== height) {
				const newArray = [...state.banners];
				// Replace rather than mutate: mutating would let the previous state observe the new
				// height, defeating any memoisation downstream.
				newArray[index] = { name, height };
				return { ...state, banners: newArray };
			}
		});
	};

	/**
	 * Removes the specified banner from the list of visible banners
	 * Remove the banner if it is in the list, do nothing it is not
	 *
	 * @param {string} name Unique name of the banner
	 */
	hide = (name: string): void => {
		if (this.storeBacked) {
			if (!this.suppressed) {
				hideBanner(name);
			}
			return;
		}

		void this.setState((state: any) => {
			const index = findBanner(name, state.banners);

			if (index > -1) {
				const newArray = [...state.banners];
				newArray.splice(index, 1);
				return { ...state, banners: newArray };
			}
		});
	};

	/**
	 * Returns the combined height of all banners in the list.
	 * @param {boolean} includeTopNav Include `TOP_NAV_HEIGHT` in the calculation
	 * if not `false`
	 * @returns {number} Combined height of all visible banners
	 */
	getTotalHeight = (includeTopNav?: boolean): number =>
		this.state.banners.reduce(
			(totalOffset: number, banner: BannerStateInfo) => totalOffset + banner.height,
			includeTopNav !== false ? this.topNavHeight : 0,
		);

	/**
	 * Returns list of all banners in visible list in "-" seperated string.
	 * @returns {string} Combined names of all banners in visible list seperated by "-"
	 */
	getBannerList = (): string => this.state.banners.map((banner) => banner.name).join('-');

	/**
	 * Detaches from the store. unstated gives containers no teardown hook, so any caller that creates
	 * short-lived containers — fixtures, tests, anything mounted more than once — must call this, or
	 * the store keeps the listener and with it this instance alive for the lifetime of the module.
	 */
	destroy = (): void => {
		this.unsubscribeFromStore?.();
		this.unsubscribeFromStore = null;
	};

	private scheduleNotify = (): void => {
		if (this.pendingNotify) {
			return;
		}

		this.pendingNotify = Promise.resolve().then(() => {
			this.pendingNotify = null;
			return this.notifyListeners();
		});
	};
}
